import { Field, Select } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import {
  changedField,
  DISABLED_EXCLUSIONS,
  DISABLED_SELECTORS,
  fieldBorder,
  focusRing,
  hoverable,
} from '@/design-system/interaction-states';
import { forHover, forMobile } from '@/design-system/responsive';
import { scrollbarStyles } from '@/design-system/scrollbar';

import { shadows } from '../../shadows';
import { typographyVariantStyles } from '../../typography-variants';
import type { HelperState } from '../Helper/types';
import type { SelectSize } from './types';

const sizeConfig: Record<
  SelectSize,
  { padding: string; height: number; borderRadius: number; typography: 'body2' | 'body3' }
> = {
  sm: { padding: '6px 12px', height: 32, borderRadius: 8, typography: 'body3' },
  md: { padding: '8px 12px', height: 36, borderRadius: 12, typography: 'body3' },
  lg: { padding: '8px 12px', height: 40, borderRadius: 12, typography: 'body2' },
  xl: { padding: '12px 16px', height: 48, borderRadius: 12, typography: 'body2' },
};

function getBorderColor(theme: Theme, state: HelperState): string {
  switch (state) {
    case 'destructive':
      return theme.colors.error.border;
    case 'success':
      return theme.colors.success.border;
    default:
      return theme.colors.border.default;
  }
}

// ─── Trigger ─────────────────────────────────────────────────────────────────

export const StyledRoot = styled(Field.Root, {
  shouldForwardProp: (prop) => prop !== '$inline',
})<{ $inline?: boolean }>(({ theme, $inline }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: $inline ? 'auto' : '100%',
  flexShrink: $inline ? 0 : undefined,

  '&[data-disabled]': {
    opacity: 0.5,
    pointerEvents: 'none',
  },

  ...changedField(theme),
}));

const triggerStyles = (theme: Theme, size: SelectSize, state: HelperState) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box' as const,
  padding: sizeConfig[size].padding,
  height: sizeConfig[size].height,
  borderRadius: sizeConfig[size].borderRadius,
  // Hover moves the border, not the fill; success/destructive keep their own border instead.
  ...hoverable(theme, state === 'default' ? 'border' : 'surfaceStrong'),
  backgroundColor: theme.colors.background.overlay,
  border: `1px solid ${fieldBorder(getBorderColor(theme, state))}`,
  outline: 'none',
  cursor: 'pointer',
  textAlign: 'left' as const,
  appearance: 'none' as const,
  fontFamily: 'inherit',

  '&[data-popup-open]': {
    borderColor: theme.colors.border.brand,
  },

  ...focusRing(theme),

  [DISABLED_SELECTORS]: {
    cursor: 'not-allowed',
  },
});

export const StyledTrigger = styled(Select.Trigger, {
  shouldForwardProp: (prop) => !['$size', '$state'].includes(prop as string),
})<{ $size: SelectSize; $state: HelperState }>(({ theme, $size, $state }) =>
  triggerStyles(theme, $size, $state),
);

export const StyledTriggerButton = styled('button', {
  shouldForwardProp: (prop) => !['$size', '$state', '$collapseOnMobile'].includes(prop as string),
})<{ $size: SelectSize; $state: HelperState; $collapseOnMobile?: boolean }>(
  ({ theme, $size, $state, $collapseOnMobile }) => ({
    ...triggerStyles(theme, $size, $state),
    width: 'auto',
    flexShrink: 0,

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },

    ...($collapseOnMobile && {
      [forMobile]: {
        width: sizeConfig[$size].height,
        height: sizeConfig[$size].height,
        padding: 0,
        justifyContent: 'center',
        borderRadius: theme.radius[8],
        backgroundColor: theme.colors.black[4],

        '[data-trigger-text], [data-trigger-chevron]': {
          display: 'none',
        },
      },
    }),
  }),
);

export const StyledIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: theme.colors.icon.primary,

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

const valueStyles = (theme: Theme, size: SelectSize) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
  color: theme.colors.text.primary,
  ...typographyVariantStyles[sizeConfig[size].typography],
});

export const StyledValue = styled(Select.Value, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: SelectSize }>(({ theme, $size }) => valueStyles(theme, $size));

export const StyledTriggerButtonValue = styled('span', {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: SelectSize }>(({ theme, $size }) => valueStyles(theme, $size));

export const StyledValueText = styled('span')({
  flex: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const StyledBadgeWrapper = styled('span')({
  flexShrink: 0,
  display: 'inline-flex',
});

const chevronStyles = (theme: Theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: theme.colors.icon.primary,
  transition: 'transform 0.15s ease',
});

export const StyledChevronIcon = styled(Select.Icon)(({ theme }) => ({
  ...chevronStyles(theme),

  '&[data-popup-open]': {
    transform: 'rotate(180deg)',
  },
}));

export const StyledTriggerButtonChevron = styled('span', {
  shouldForwardProp: (prop) => prop !== '$open',
})<{ $open?: boolean }>(({ theme, $open }) => ({
  ...chevronStyles(theme),
  transform: $open ? 'rotate(180deg)' : undefined,

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

// ─── Popup ────────────────────────────────────────────────────────────────────

export const StyledPositioner = styled(Select.Positioner)({
  zIndex: 1400,
});

export const StyledPopup = styled(Select.Popup)(({ theme }) => ({
  backgroundColor: theme.colors.background.raised,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[8],
  padding: 4,
  boxShadow: shadows.md,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 'var(--anchor-width)',
  maxWidth: 'max(var(--anchor-width), 280px)',
  maxHeight: 320,
  outline: 'none',
  transformOrigin: 'var(--transform-origin)',
  transition: 'opacity 0.15s ease, transform 0.15s ease',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.97) translateY(-4px)',
  },
}));

// ─── Search ───────────────────────────────────────────────────────────────────

export const StyledSearchWrapper = styled('div')({
  flexShrink: 0,
});

// ─── Select All / Clear ────────────────────────────────────────────────────────

export const StyledActionsRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
});

const dividerStyles = (theme: Theme) => ({
  flexShrink: 0,
  position: 'relative' as const,
  height: 8,

  '&::before': {
    content: '""',
    position: 'absolute' as const,
    top: '50%',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: theme.colors.border.default,
  },
});

export const StyledSeparator = styled('div')(({ theme }) => dividerStyles(theme));

// ─── List & Items ───────────────────────────────────────────────────────────────

export const StyledList = styled(Select.List)({
  overflow: 'auto',
  flex: 1,
  ...scrollbarStyles,
});

// Stands in for the list when there is nothing to pick — either the consumer
// passed no options, or the search matched none of them. Without it the popup
// renders as an empty box (just the search field over blank space).
export const StyledEmpty = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 12,
  textAlign: 'center',
  ...typographyVariantStyles.body3,
  color: theme.colors.text.tertiary,
}));

export const StyledItem = styled(Select.Item)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 8,
  cursor: 'pointer',
  borderRadius: theme.radius[8],
  outline: 'none',
  userSelect: 'none',
  ...typographyVariantStyles.body2,
  color: theme.colors.text.primary,
  transition: theme.motion.hover,

  '&[data-highlighted]': {
    backgroundColor: theme.colors.background.overlay,
  },

  [forHover]: {
    [`&:hover${DISABLED_EXCLUSIONS}`]: {
      backgroundColor: theme.colors.background.overlay,
    },
  },

  [`&:active${DISABLED_EXCLUSIONS}`]: {
    backgroundColor: theme.colors.neutral.active,
  },

  '&[data-selected]': {
    color: theme.colors.text.brand,
  },

  ...focusRing(theme),

  [DISABLED_SELECTORS]: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
}));

export const StyledItemIconWrapper = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 20,
  height: 20,

  '& svg': {
    width: '100%',
    height: '100%',
  },
});

export const StyledItemTextRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flex: 1,
  minWidth: 0,
});

export const StyledItemText = styled(Select.ItemText)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StyledItemSupportText = styled('span')(({ theme }) => ({
  flexShrink: 0,
  color: theme.colors.text.secondary,
  ...typographyVariantStyles.body4,
}));

export const StyledItemIndicator = styled(Select.ItemIndicator)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 20,
  height: 20,

  svg: {
    width: '100%',
    height: '100%',
  },
});

export const StyledCheckboxWrapper = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,
  padding: 2,
});

// ─── Groups ───────────────────────────────────────────────────────────────────

export const StyledGroup = styled(Select.Group)({
  '& + &': {
    marginTop: 4,
  },
});

export const StyledGroupLabel = styled(Select.GroupLabel, {
  shouldForwardProp: (prop) => prop !== '$indented',
})<{ $indented?: boolean }>(({ theme, $indented }) => ({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,
  padding: 8,
  paddingLeft: $indented ? 40 : 8,
  userSelect: 'none',
}));

export const StyledGroupSeparator = styled(Select.Separator)(({ theme }) => dividerStyles(theme));
